import { ObjectManager } from '../../../concepts/objects';
import { Line } from '../../../platform/geom/Line';
import { Point } from '../../../platform/geom/Point';
import { Rect } from '../../../platform/geom/Rect';
import { MouseButtons, MouseInput } from '../../../platform/input/MouseInput';
import { remInPx } from '../../../ui';
import { Camera } from '../../canvas/Camera';
import { EditorSession } from '../../canvas/EditorSession';
import { RelativeTo } from '../../canvas/entities/Physical';
import { defineToolWithCommonBehaviors } from '../../canvas/tooling/Definitions';
import { ToolBehavior } from '../../canvas/tooling/ToolBehavior';
import { SpatialIndex } from '../../canvas/utilities/SpatialIndex';
import { Graffiti, GraffitiTarget } from '../../canvas/visualization/Graffiti';
import { resolve } from '../../Container';
import { ReactionIcon } from '../reactions/ReactionIcon';
import { ActiveSketchInfo } from './ActiveSketchInfo';
import { Sketch } from './Sketch';
import { SketchStroke } from './SketchStroke';

type EraserMode = 'passive' | 'active';

class AbstractEraserToolBehavior extends ToolBehavior {

    @resolve(Camera) private camera!: Camera;
    @resolve(ObjectManager) private objects!: ObjectManager;
    @resolve(EditorSession) private session!: EditorSession;
    @resolve(SpatialIndex) private spatial!: SpatialIndex;

    private counter: number = 0;
    private prevPoint: Point | null = null;

    constructor(private mode: EraserMode) {
        super();
    }

    public getPreferredCursor(state: string) {
        if (state === 'erasing' || this.mode === 'active') {
            return 'eraser';
        }
    }

    public onMouseDown(state: string, mi: MouseInput): string | void {

        if (state !== 'idle') return;
        if (this.mode === 'active' && mi.button !== MouseButtons.Main) return;
        if (this.mode === 'passive' && mi.button !== MouseButtons.Secondary) return;

        // Look for an active sketch (might not have one)
        const maybeSketch = this.tryGetActiveSketch();

        // Only start passive if we are targetting a valid subject
        if (this.mode === 'passive') {
            if (maybeSketch?.alive) {
                if (!this.isPointInActiceSketchArea(mi.position, maybeSketch)) return;
            }
            else {
                const subject = mi.targetPhysical;
                if (!subject?.is(GraffitiTarget) && !subject?.is(Sketch)) return;
            }
        }

        this.counter++;

        const mpt = this.camera.toModelPoint(mi.position);
        this.performEraseBetween(mpt, null, maybeSketch);
        this.prevPoint = mpt;

        return 'erasing';
    }

    public onMouseMove(state: string, mi: MouseInput): string | void {

        if (state !== 'erasing') {
            return;
        }

        const mpt = this.camera.toModelPoint(mi.position);
        const maybeSketch = this.tryGetActiveSketch();

        this.performEraseBetween(mpt, this.prevPoint, maybeSketch);
        this.prevPoint = mpt;
    }

    public onMouseUp(state: string, mi: MouseInput): string | void {
        const button = this.mode === 'active' ? MouseButtons.Main : MouseButtons.Secondary;

        if (state === 'erasing' && mi.button === button) {
            this.objects.update(u => u.noop());
            return 'idle';
        }
    }

    private performEraseBetween(a: Point, b?: Point, sketch?: Sketch) {

        b = b ?? a.add([1, 1]);

        const ln = new Line(a, b);
        const area = Rect.fromPoints([a, b]);
        const hits = Array.from((!!sketch) ? [sketch] : this.spatial.lookup(area));

        for (const tm of hits) {
            if (!tm.alive) continue;

            if (tm.is(Sketch)) {
                this.performEraseOnSketch(tm.as(Sketch), ln);
            }
            else if (tm.is(ReactionIcon)) {
                this.performEraseOfIcon(tm.as(ReactionIcon));
            }
            else {
                this.performEraseOnPhysical(tm, ln);
            }
        }
    }

    private performEraseOnSketch(subject: Sketch, ln: Line) {

        const rln = subject.toRelative(ln);
        const hits = subject.layout.paths
            .filter(x => x.path.intersects(rln) || x.path.contains(rln.p1) || x.path.contains(rln.p2))
            .map(x => this.objects.get(x.id, SketchStroke));

        if (!hits.length) return;

        this.objects.update(u => {
            u.rolling(`erase:${this.counter}`);
            for (const x of hits) {
                subject.truncate(x);
                u.delete(x);
            }
            if (!subject.polarize(SketchStroke).length) {
                u.delete(subject.removeSelf());
            }
        });
    }

    private performEraseOnPhysical(subject: GraffitiTarget, ln: Line) {

        const rln = subject.toRelative(ln);
        const hits = new Set<Graffiti>();

        for (const ch of subject.children) {
            const gr = ch.as(Graffiti);
            if (!gr?.alive) continue;
            const ol = gr.outline();
            if (ol.intersects(rln) || ol.contains(rln.p1) || ol.contains(rln.p2)) {
                hits.add(gr);
            }
        }

        if (!hits.size) return;

        this.objects.update(u => {
            u.rolling(`erase:${this.counter}`);
            for (const x of hits) {
                u.delete(x.removeSelf());
            }
        });
    }

    private performEraseOfIcon(subject: ReactionIcon) {

        this.objects.update(u => {
            u.rolling(`erase:${this.counter}`);
            u.delete(subject.removeSelf());
        });
    }

    private isPointInActiceSketchArea(vpt: Point, sketch: Sketch) {
        const sketchBounds = sketch.bounds(RelativeTo.Model);
        const area = Rect.fromPoints(this.camera.toView(sketchBounds))
            .inflate(remInPx(4));

        return area.contains(vpt) || sketchBounds.contains(this.camera.toModelPoint(vpt));
    }

    private tryGetActiveSketch() {
        const { sketchId } = this.session.get(ActiveSketchInfo);
        return this.objects.getMaybe(sketchId, Sketch);
    }
}

export const EraserToolBehavior = (mode: EraserMode) =>
    class extends AbstractEraserToolBehavior {
        constructor() {
            super(mode);
        }
    };


export const eraserTool = defineToolWithCommonBehaviors({
    id: 'eraserTool',
    label: 'Eraser',
    category: 'misc',
    behaviors: [
        EraserToolBehavior('active'),
    ]
});
